// src/pages/RestaurantOwner/AuditDashboard.jsx - FRAUD PREVENTION & AUDIT

import React, { useState, useEffect, useMemo } from 'react';
import { 
  Shield, AlertTriangle, TrendingUp, TrendingDown, Download, 
  Clock, XCircle, CheckCircle, Loader, Calendar, Filter,
  User, DollarSign, FileText, BarChart3, PieChart as PieChartIcon
} from 'lucide-react';
import StorageService from '../../services/StorageService';
import { 
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

const COLORS = ['#ef4444', '#f97316', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6'];
const RISK_COLORS = {
  high: 'bg-red-100 text-red-700 border-red-300',
  medium: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  low: 'bg-green-100 text-green-700 border-green-300'
};

const AuditDashboard = ({ restaurantId }) => {
  const [invoices, setInvoices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30'); // 1, 7, 30, 90 days
  const [userFilter, setUserFilter] = useState('all');
  const [restaurant, setRestaurant] = useState(null);
  // Helper function to calculate "time ago"
  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    const now = new Date();
    const past = new Date(timestamp);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return past.toLocaleDateString('en-IN');
  };

  useEffect(() => {
    loadAuditData();
  }, [restaurantId]);

  const loadAuditData = async () => {
    try {
      setLoading(true);
      const [allInvoices, restaurant] = await Promise.all([
        StorageService.getInvoices(restaurantId),
        StorageService.getRestaurant(restaurantId)
      ]);

      setInvoices(allInvoices || []);
      setOrders(restaurant?.orders || []);
      setRestaurant(restaurant);
    } catch (error) {
      console.error('Error loading audit data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter data by time range
  const filteredData = useMemo(() => {
    const now = new Date();
    const daysBack = parseInt(timeRange);
    const startDate = new Date(now.getTime() - (daysBack * 24 * 60 * 60 * 1000));

    const filteredInvoices = invoices.filter(inv => {
      const invDate = new Date(inv.timestamp);
      return invDate >= startDate;
    });

    const filteredOrders = orders.filter(order => {
      const orderDate = new Date(order.time);
      return orderDate >= startDate;
    });

    return { invoices: filteredInvoices, orders: filteredOrders };
  }, [invoices, orders, timeRange]);

  // Calculate comprehensive statistics
  const statistics = useMemo(() => {
    const { invoices: invs, orders: ords } = filteredData;

    // Invoice statistics
    const totalInvoices = invs.length;
    const cancelledInvoices = invs.filter(i => i.cancelled);
    const activeInvoices = invs.filter(i => !i.cancelled);
    
    const totalSales = activeInvoices.reduce((sum, i) => sum + (i.grandTotal || 0), 0);
    const cancelledAmount = cancelledInvoices.reduce((sum, i) => sum + (i.grandTotal || 0), 0);
    const cancellationRate = totalInvoices > 0 ? (cancelledInvoices.length / totalInvoices * 100) : 0;

    // Order statistics
    const totalOrders = ords.length;
    const cancelledOrders = ords.filter(o => o.cancelled);
    const completedOrders = ords.filter(o => o.status === 'Delivered' && !o.cancelled);
    const orderCancellationRate = totalOrders > 0 ? (cancelledOrders.length / totalOrders * 100) : 0;

    // Cancellation reasons breakdown (invoices)
    const invoiceReasons = {};
    cancelledInvoices.forEach(inv => {
      const reason = inv.cancellationReason || 'Unknown';
      invoiceReasons[reason] = (invoiceReasons[reason] || 0) + 1;
    });

    // Cancellation reasons breakdown (orders)
    const orderReasons = {};
    cancelledOrders.forEach(order => {
      const reason = order.cancellationReason || 'Unknown';
      orderReasons[reason] = (orderReasons[reason] || 0) + 1;
    });

    // User-wise cancellation tracking
    const userStats = {};
    
    cancelledInvoices.forEach(inv => {
      const user = inv.cancelledBy || inv.cashier || 'Unknown';
      if (!userStats[user]) {
        userStats[user] = { 
          name: user, 
          invoiceCancellations: 0, 
          orderCancellations: 0,
          amount: 0 
        };
      }
      userStats[user].invoiceCancellations++;
      userStats[user].amount += inv.grandTotal || 0;
    });

    cancelledOrders.forEach(order => {
      const user = order.cancelledBy || 'Unknown';
      if (!userStats[user]) {
        userStats[user] = { 
          name: user, 
          invoiceCancellations: 0, 
          orderCancellations: 0,
          amount: 0 
        };
      }
      userStats[user].orderCancellations++;
    });

    // Risk assessment
    const suspiciousActivity = [];

    // High-value cancellations (>₹2000)
    cancelledInvoices.forEach(inv => {
      if (inv.grandTotal > 2000) {
        suspiciousActivity.push({
          type: 'invoice',
          id: inv.invoiceNumber,
          risk: 'high',
          reason: 'High Value Cancellation',
          amount: inv.grandTotal,
          user: inv.cancelledBy || inv.cashier,
          time: inv.cancelledAt,
          details: inv.cancellationReason
        });
      }
    });

    // Multiple cancellations by same user (more than 3 in period)
    Object.values(userStats).forEach(user => {
      const totalCancellations = user.invoiceCancellations + user.orderCancellations;
      if (totalCancellations > 3) {
        suspiciousActivity.push({
          type: 'pattern',
          risk: totalCancellations > 5 ? 'high' : 'medium',
          reason: 'Multiple Cancellations by User',
          user: user.name,
          count: totalCancellations,
          amount: user.amount
        });
      }
    });

    // Trend analysis
    const dailyData = {};
    
    cancelledInvoices.forEach(inv => {
      const date = new Date(inv.cancelledAt).toLocaleDateString('en-IN');
      if (!dailyData[date]) {
        dailyData[date] = { date, invoices: 0, orders: 0, amount: 0 };
      }
      dailyData[date].invoices++;
      dailyData[date].amount += inv.grandTotal || 0;
    });

    cancelledOrders.forEach(order => {
      const date = new Date(order.cancelledAt).toLocaleDateString('en-IN');
      if (!dailyData[date]) {
        dailyData[date] = { date, invoices: 0, orders: 0, amount: 0 };
      }
      dailyData[date].orders++;
    });

    const trendData = Object.values(dailyData).sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );

    return {
      totalInvoices,
      activeInvoices: activeInvoices.length,
      cancelledInvoices: cancelledInvoices.length,
      cancellationRate,
      totalSales,
      cancelledAmount,
      totalOrders,
      completedOrders: completedOrders.length,
      cancelledOrders: cancelledOrders.length,
      orderCancellationRate,
      invoiceReasons,
      orderReasons,
      userStats: Object.values(userStats),
      suspiciousActivity,
      trendData
    };
  }, [filteredData]);

  // Prepare chart data
  const reasonChartData = useMemo(() => {
    return Object.entries(statistics.invoiceReasons).map(([reason, count]) => ({
      name: reason.length > 20 ? reason.substring(0, 20) + '...' : reason,
      value: count,
      fullName: reason
    }));
  }, [statistics.invoiceReasons]);

  const userChartData = useMemo(() => {
    let data = statistics.userStats;
    if (userFilter !== 'all') {
      data = data.filter(u => u.name === userFilter);
    }
    return data.map(user => ({
      name: user.name.length > 15 ? user.name.substring(0, 15) + '...' : user.name,
      invoices: user.invoiceCancellations,
      orders: user.orderCancellations,
      amount: user.amount
    }));
  }, [statistics.userStats, userFilter]);

  const exportAuditReport = () => {
    let csv = 'FRAUD PREVENTION & AUDIT REPORT\n';
    csv += `Generated: ${new Date().toLocaleString('en-IN')}\n`;
    csv += `Period: Last ${timeRange} days\n\n`;

    csv += 'SUMMARY STATISTICS\n';
    csv += `Total Invoices,${statistics.totalInvoices}\n`;
    csv += `Cancelled Invoices,${statistics.cancelledInvoices}\n`;
    csv += `Cancellation Rate,${statistics.cancellationRate.toFixed(2)}%\n`;
    csv += `Cancelled Amount,₹${statistics.cancelledAmount.toFixed(2)}\n`;
    csv += `Total Orders,${statistics.totalOrders}\n`;
    csv += `Cancelled Orders,${statistics.cancelledOrders}\n\n`;

    csv += 'CANCELLATION REASONS (INVOICES)\n';
    csv += 'Reason,Count\n';
    Object.entries(statistics.invoiceReasons).forEach(([reason, count]) => {
      csv += `"${reason}",${count}\n`;
    });
    csv += '\n';

    csv += 'USER STATISTICS\n';
    csv += 'User,Invoice Cancellations,Order Cancellations,Total Amount\n';
    statistics.userStats.forEach(user => {
      csv += `"${user.name}",${user.invoiceCancellations},${user.orderCancellations},₹${user.amount.toFixed(2)}\n`;
    });
    csv += '\n';

    csv += 'SUSPICIOUS ACTIVITY\n';
    csv += 'Risk Level,Type,Details\n';
    statistics.suspiciousActivity.forEach(activity => {
      csv += `${activity.risk.toUpperCase()},"${activity.reason}","${activity.details || activity.count || ''}"\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Audit_Report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading audit data...</p>
        </div>
      </div>
    );
  }

  if (!loading && restaurant) {
    const plan = restaurant.package?.toLowerCase();
    const hasAuditAccess = plan === 'premium' || plan === 'advanced' || plan==='basic';
    if (!hasAuditAccess) {
        return (
            <div className="p-6 min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
                <div className="max-w-2xl mx-auto mt-20">
                    <div className="bg-white rounded-2xl shadow-xl p-8 text-center border-4 border-blue-200">
                        <div className="bg-red-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Shield className="w-10 h-10 text-red-600" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800 mb-4">Audit Dashboard Access Restricted</h2>
                        <p className="text-lg text-gray-600 mb-2">
                            You are currently on the <span className="font-bold text-orange-600">{restaurant.package || 'Basic'} Plan</span>
                        </p>
                        <p className="text-gray-600 mb-8">
                            Fraud Prevention &amp; Audit Dashboard is only available for <span className="font-bold">Premium</span> and <span className="font-bold">Advanced</span> plan users.
                        </p>
                        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 mb-8">
                            <h3 className="font-bold text-gray-800 mb-4">Upgrade to access:</h3>
                            <ul className="text-left space-y-2 text-gray-700">
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    Track All Cancellations (Invoices &amp; Orders)
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    Detect High-Value Cancellations
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    User-wise Cancellation Analytics
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    Suspicious Activity Detection
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    Exportable Audit Reports
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    Fraud Prevention Insights
                                </li>
                            </ul>
                        </div>
                        <a
                            href={'mailto:contact@zyppro.com?subject=Upgrade%20to%20Premium%20Plan&body=Hi%20Zyppro%20Team,%0D%0A%0D%0AI%20would%20like%20to%20upgrade%20to%20Premium%20or%20Advanced%20plan%20to%20access%20Audit%20Dashboard.%0D%0A%0D%0ARestaurant:%20'+ encodeURIComponent(restaurant.name) + '%0D%0ACurrent%20Plan:%20' + encodeURIComponent(restaurant.package || 'Basic') +'%0D%0A%0D%0APlease%20contact%20me.%0D%0A%0D%0AThank%20you'}
                            className="inline-flex items-center gap-2 bg-blue-500 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-600 transition shadow-lg"
                        >
                            <Shield className="w-6 h-6" />
                            Contact Us to Upgrade
                        </a>
                        <p className="text-sm text-gray-600 mt-4">
                            Email: <a href="mailto:contact@zyppro.com" className="text-blue-600 underline font-semibold">contact@zyppro.com</a>
                        </p>
                    </div>
                </div>
            </div>
        );
    }
}

  return (
    <div className="p-4 md:p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
              <Shield className="w-8 h-8 text-blue-600" />
              Fraud Prevention & Audit
            </h1>
            <p className="text-gray-600 mt-1">Monitor cancellations and detect suspicious activity</p>
          </div>
          <div className="flex gap-2">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="1">Last 24 Hours</option>
              <option value="7">Last 7 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="90">Last 90 Days</option>
            </select>
            <button
              onClick={exportAuditReport}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
            >
              <Download className="w-5 h-5" />
              <span className="hidden md:inline">Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Alert for suspicious activity */}
      {statistics.suspiciousActivity.length > 0 && (
        <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 mb-6 shadow-md">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-bold text-red-800 text-lg">⚠️ Suspicious Activity Detected</h3>
              <p className="text-red-700 mt-1">
                {statistics.suspiciousActivity.length} high-risk event(s) require your attention. Review details below.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-green-500">
          <div className="flex items-center justify-between mb-2">
            <CheckCircle className="w-8 h-8 text-green-500" />
            <div className="text-right">
              <p className="text-gray-600 text-sm">Total Sales</p>
              <p className="text-2xl font-bold text-gray-800">₹{statistics.totalSales.toLocaleString()}</p>
            </div>
          </div>
          <p className="text-xs text-gray-500">{statistics.activeInvoices} active invoices</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-blue-500">
          <div className="flex items-center justify-between mb-2">
            <FileText className="w-8 h-8 text-blue-500" />
            <div className="text-right">
              <p className="text-gray-600 text-sm">Total Orders</p>
              <p className="text-2xl font-bold text-gray-800">{statistics.totalOrders}</p>
            </div>
          </div>
          <p className="text-xs text-gray-500">{statistics.completedOrders} completed</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-orange-500">
          <div className="flex items-center justify-between mb-2">
            <XCircle className="w-8 h-8 text-orange-500" />
            <div className="text-right">
              <p className="text-gray-600 text-sm">Cancelled Invoices</p>
              <p className="text-2xl font-bold text-gray-800">{statistics.cancelledInvoices}</p>
            </div>
          </div>
          <p className="text-xs text-gray-500">{statistics.cancellationRate.toFixed(1)}% cancellation rate</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-md border-l-4 border-red-500">
          <div className="flex items-center justify-between mb-2">
            <DollarSign className="w-8 h-8 text-red-500" />
            <div className="text-right">
              <p className="text-gray-600 text-sm">Cancelled Amount</p>
              <p className="text-2xl font-bold text-gray-800">₹{statistics.cancelledAmount.toLocaleString()}</p>
            </div>
          </div>
          <p className="text-xs text-gray-500">
            {((statistics.cancelledAmount / (statistics.totalSales + statistics.cancelledAmount)) * 100).toFixed(1)}% of potential revenue
          </p>
        </div>
      </div>

      {/* Suspicious Activity Table */}
      {statistics.suspiciousActivity.length > 0 && (
        <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-red-500 to-red-600 p-4">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <AlertTriangle className="w-6 h-6" />
              Suspicious Activity (Requires Review)
            </h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Risk</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Invoice/Type</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Flag Reason</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">User</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Time</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Amount/Count</th>
                </tr>
              </thead>
              <tbody>
                {statistics.suspiciousActivity.map((activity, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50 transition">
                    <td className="px-4 py-3">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold border ${RISK_COLORS[activity.risk]}`}>
                        {activity.risk.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-bold text-gray-800">
                          {activity.type === 'invoice' ? `#${activity.id}` : activity.type.toUpperCase()}
                        </p>
                        <p className="text-xs text-gray-500">
                          {activity.type === 'invoice' ? 'Invoice' : 'Pattern'}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{activity.reason}</p>
                      {activity.details && (
                        <p className="text-xs text-gray-500 mt-1">{activity.details}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-700 font-medium">{activity.user || 'N/A'}</td>
                    <td className="px-4 py-3 text-gray-600 text-sm">
                      {activity.time ? (
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {getTimeAgo(activity.time)}
                        </div>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 font-bold text-orange-600">
                      {activity.amount ? `₹${activity.amount.toLocaleString()}` : activity.count || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Cancellation Trend */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <TrendingDown className="w-5 h-5 text-red-500" />
            Cancellation Trend
          </h2>
          {statistics.trendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={statistics.trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="invoices" stroke="#ef4444" name="Invoices" strokeWidth={2} />
                <Line type="monotone" dataKey="orders" stroke="#f97316" name="Orders" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-gray-500 py-12">No cancellation data</p>
          )}
        </div>

        {/* Cancellation Reasons */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <PieChartIcon className="w-5 h-5 text-purple-500" />
            Cancellation Reasons (Invoices)
          </h2>
          {reasonChartData.length > 0 ? (
            <div>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={reasonChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {reasonChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="grid grid-cols-2 gap-2 mt-4">
                {reasonChartData.map((item, index) => (
                  <div key={index} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                    <div 
                      className="w-3 h-3 rounded-full flex-shrink-0" 
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-gray-800 truncate" title={item.fullName}>
                        {item.name}
                      </p>
                      <p className="text-xs text-gray-600">{item.value} times</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-12">No cancellation data</p>
          )}
        </div>
      </div>

      {/* User Statistics */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <User className="w-5 h-5 text-blue-500" />
            User-wise Cancellation Statistics
          </h2>
          <select
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">All Users</option>
            {statistics.userStats.map(user => (
              <option key={user.name} value={user.name}>{user.name}</option>
            ))}
          </select>
        </div>
        {userChartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={userChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="invoices" fill="#ef4444" name="Invoice Cancellations" />
              <Bar dataKey="orders" fill="#f97316" name="Order Cancellations" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-center text-gray-500 py-12">No user data</p>
        )}
      </div>

      {/* Best Practices */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 border-l-4 border-green-500 rounded-xl p-6 shadow-md">
        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Shield className="w-6 h-6 text-green-600" />
          Anti-Fraud Best Practices
        </h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <p className="font-semibold text-gray-700">✅ What This Dashboard Does:</p>
            <ul className="space-y-1 text-gray-600">
              <li>• Tracks all cancellations (invoices & orders)</li>
              <li>• Identifies high-value cancellations (above ₹2000)</li>
              <li>• Flags users with multiple cancellations</li>
              <li>• Shows cancellation trends over time</li>
              <li>• Provides exportable audit reports</li>
            </ul>
          </div>
          <div className="space-y-2">
            <p className="font-semibold text-gray-700">🔒 Additional Recommendations:</p>
            <ul className="space-y-1 text-gray-600">
              <li>• Conduct daily cash reconciliation</li>
              <li>• Review high-risk cancellations immediately</li>
              <li>• Compare expected vs actual cash received</li>
              <li>• Investigate users with more than 5 cancellations</li>
              <li>• Keep CCTV footage for 30 days minimum</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditDashboard;